// Rollover chain rules — platform-policy side card.
//
// The rule list is platform policy copy carried over from the prototype
// verbatim. These are not user values — they are the doctrinal posture
// of every Phantom rollover chain (auto-protect, manual placement, etc.)
// and must remain visible to keep the consent surface honest.
//
// Visuals: Football Products (FpKit) Kalshi-clean — inline FpKit tokens,
// hairline borders, mono uppercase labels, no gradients.

import { Icon } from "../../components/icons/Icon.tsx";

interface Rule {
  readonly lab: string;
  readonly det: string;
}

const RULES: readonly Rule[] = [
  {
    lab: "Compound on win, reset on loss",
    det: "Each won week's winnings become next week's stake. A loss ends the chain — no chasing.",
  },
  {
    lab: "Auto-protect a share of every win",
    det: "Confirmed protection moves to your Vault on each win. You can't lose what's been protected.",
  },
  {
    lab: "Cap, then payout",
    det: "Reaching the chain length closes the cycle and pays out — even if the chain could keep growing.",
  },
  {
    lab: "Loss limit per week",
    det: "Safe Mode rejects any week where exposure would exceed your set limit.",
  },
  {
    lab: "Manual placement, always",
    det: "Strikza never places, logs in, or holds funds. You're the only finger on the trigger.",
  },
];

export interface RolloverChainRulesProps {
  readonly setOn: string | null;
}

export function RolloverChainRules({ setOn }: RolloverChainRulesProps): JSX.Element {
  return (
    <div
      style={{
        background: "var(--bg-elev)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        padding: 14,
      }}
    >
      {/* Card head */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          paddingBottom: 12,
          marginBottom: 12,
          borderBottom: "1px solid var(--border-faint)",
        }}
      >
        <span style={{ color: "var(--fp-accent-text)", display: "inline-flex" }}>
          <Icon name="info" size={14} />
        </span>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              color: "var(--fp-accent-text)",
            }}
          >
            Doctrine
          </div>
          <h3
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 18,
              lineHeight: 1,
              margin: 0,
              color: "var(--text)",
              letterSpacing: "0.01em",
            }}
          >
            Chain rules
          </h3>
        </div>
        {setOn !== null ? (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "var(--text-tertiary)",
              whiteSpace: "nowrap",
            }}
          >
            Set on {setOn}
          </span>
        ) : null}
      </div>

      {/* Rules list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {RULES.map((r, i) => (
          <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <div
              style={{
                flexShrink: 0,
                width: 20,
                height: 20,
                borderRadius: 5,
                background: "var(--bg-inset)",
                border: "1px solid var(--border-faint)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--fp-won)",
                marginTop: 1,
              }}
            >
              <Icon name="check" size={12} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: "var(--text)",
                  lineHeight: 1.3,
                }}
              >
                {r.lab}
              </div>
              <div
                style={{
                  fontSize: 11.5,
                  color: "var(--text-tertiary)",
                  lineHeight: 1.45,
                  marginTop: 2,
                }}
              >
                {r.det}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
